/**
 * Operator-facing Song Processing load errors.
 * Never surface opaque gateway/Lambda text like "Internal Server Error".
 */

/** Messages that must not be shown raw to operators. */
const OPAQUE_SERVER_MESSAGES = new Set([
  "internal server error",
  "internal error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
  "endpoint request timed out",
  "network error",
  "failed to fetch",
]);

export const SONG_PROCESSING_LOAD_FAILURE_TITLE = "Song Processing could not be loaded.";
export const SONG_PROCESSING_LOAD_FAILURE_BODY =
  "Please try again. If the problem continues, contact KrossKast support.";

export function isOpaqueServerErrorMessage(message: string | null | undefined): boolean {
  if (!message) return true;
  const normalized = message.trim().toLowerCase();
  if (!normalized) return true;
  if (OPAQUE_SERVER_MESSAGES.has(normalized)) return true;
  // API Gateway / Lambda proxy variants
  if (/^internal\s+server\s+error\b/.test(normalized)) return true;
  if (/^http\s*5\d\d\b/.test(normalized)) return true;
  return false;
}

export function extractProcessingRequestId(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const data = anyErr.data as Record<string, unknown> | undefined;
  const meta = anyErr.meta as Record<string, unknown> | undefined;
  const response = anyErr.response as Record<string, unknown> | undefined;
  const rawHeaders =
    (meta?.response as Record<string, unknown> | undefined)?.headers ||
    response?.headers ||
    anyErr.headers;
  const headers =
    rawHeaders && typeof rawHeaders === "object"
      ? (rawHeaders as Record<string, unknown>)
      : null;

  const headerGet = (key: string): unknown => {
    if (!headers) return undefined;
    if (key in headers) return headers[key];
    const found = Object.keys(headers).find((k) => k.toLowerCase() === key.toLowerCase());
    return found ? headers[found] : undefined;
  };

  const candidates = [
    data?.requestId,
    data?.correlationId,
    data?.request_id,
    anyErr.requestId,
    anyErr.correlationId,
    headerGet("x-amzn-requestid"),
    headerGet("apigw-requestid"),
    headerGet("x-request-id"),
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

export function extractProcessingHttpStatus(err: unknown): number | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  if (typeof anyErr.status === "number") return anyErr.status;
  if (typeof anyErr.statusCode === "number") return anyErr.statusCode;
  const data = anyErr.data as Record<string, unknown> | undefined;
  if (typeof data?.statusCode === "number") return data.statusCode;
  return null;
}

/**
 * List/detail load copy for operators. Remaps opaque 5xx/gateway text.
 * Domain validation messages (4xx with specific body) still pass through
 * via extractProcessingErrorMessage when they are not opaque.
 */
export function formatSongProcessingLoadFailure(
  err: unknown,
  extractMessage: (err: unknown) => string,
): {
  title: string;
  body: string;
  technicalRequestId: string | null;
  httpStatus: number | null;
} {
  const raw = (extractMessage(err) || "").trim();
  const httpStatus = extractProcessingHttpStatus(err);
  const opaque =
    isOpaqueServerErrorMessage(raw) ||
    (httpStatus != null && httpStatus >= 500);

  return {
    title: SONG_PROCESSING_LOAD_FAILURE_TITLE,
    body: opaque
      ? SONG_PROCESSING_LOAD_FAILURE_BODY
      : raw || SONG_PROCESSING_LOAD_FAILURE_BODY,
    technicalRequestId: extractProcessingRequestId(err),
    httpStatus,
  };
}
